import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// Response shapes mirror the backend (which mirrors the legacy Node app —
// that JSON is the spec).

export interface CatalogApp {
  app_name: string;
  display_name: string | null;
  category: string | null;
  description: string | null;
  vendor: string | null;
  owner_name: string | null;
  owner_email: string | null;
  homepage_url: string | null;
  login_url: string | null;
  users_count: number | null;
  primary_team: string | null;
  entra_registrations: string[] | null;
  in_use: boolean;
  is_universal: boolean | null;
  requires_approval: boolean;
  hidden?: boolean;
  site_visible?: boolean;
  cost_matrix_usd: number | null;
  cost_actual_usd: number | null;
  cost_source: string | null;
  last_sync_at: string | null;
}

export interface AdminApp extends CatalogApp {
  team: string | null;
  status: string | null;
  url: string | null;
  owner_email: string | null;
  notes: string | null;
  display_name: string | null;
  deleted_at: string | null;
  account_count: number;
  admin_overrides: Record<string, { value: unknown; set_by: string; set_at: string }>;
}

export interface Profile {
  profile: string;
  email: string;
  name: string;
  dept: string;
  apps: string[];
  isAdmin: boolean;
  site?: boolean;
}

export interface RequestPreview {
  to: string;
  cc: string[];
  subject: string;
  body: string;
  compose_url: string;
  mailto: string;
  isHigherUp: boolean;
  manager: { email: string; name: string | null; title: string | null; source: string } | null;
}

export interface MyRequest {
  id: string;
  app_name: string;
  manager_email: string | null;
  justification: string | null;
  status: string;
  created_at: string;
  fulfilled_at: string | null;
}

export interface AdminRequest extends MyRequest {
  requester_email: string;
}

export interface AppUserRow {
  email: string;
  assigned_via: string | null;
  first_name: string;
  last_name: string;
  department: string | null;
  job_title: string | null;
  legal_entity: string | null;
  hr_active: boolean;
}

export interface HRRosterRow {
  business_email: string;
  first_name: string | null;
  last_name: string | null;
  department: string | null;
  legal_entity: string | null;
  job_title: string | null;
  country_code: string | null;
  hire_date: string | null;
  seniority_date: string | null;
  company_classification: string | null;
  employee_identifier: string | null;
}

export interface ChartPoint {
  label: string;
  value: number;
  category?: string | null;
  prior?: number;
  app_count?: number;
}

// ── transport ───────────────────────────────────────────────────────
// The SPA is served from a GCS bucket that IAP can't protect, so a fresh
// browser has no IAP session — API fetches then die on IAP's cross-origin
// login redirect (TypeError). Detect that and do ONE full-page hop through
// /login (backend, behind IAP) which completes the Entra flow and bounces
// back here with session cookies set.
function redirectThroughIapLogin(): void {
  const last = sessionStorage.getItem('iap-login-attempt');
  if (last && Date.now() - Number(last) < 60_000) return; // loop guard
  sessionStorage.setItem('iap-login-attempt', String(Date.now()));
  const next = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.assign(`/login?next=${next}`);
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, { credentials: 'include', ...init });
  } catch (err) {
    redirectThroughIapLogin();
    throw err;
  }
  if (res.status === 401) {
    redirectThroughIapLogin();
    throw new Error(`${url}: 401`);
  }
  if (!res.ok) {
    let detail = `${res.status}`;
    try {
      const body = await res.json();
      detail = body.detail ?? body.error ?? detail;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(String(detail));
  }
  return res.json() as Promise<T>;
}

const get = <T>(url: string) => request<T>(url);
const send = <T>(url: string, method: string, body?: unknown) =>
  request<T>(url, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

// ── employee queries ────────────────────────────────────────────────
export const useProfile = () =>
  useQuery({ queryKey: ['profile'], queryFn: () => get<Profile>('/profile'), retry: false });

export const useApps = () =>
  useQuery({ queryKey: ['apps'], queryFn: () => get<CatalogApp[]>('/api/apps') });


export const useMyRequests = () =>
  useQuery({ queryKey: ['my-requests'], queryFn: () => get<MyRequest[]>('/api/my-requests') });

export const useRequestPreview = (appName: string | null) =>
  useQuery({
    queryKey: ['request-preview', appName],
    queryFn: () => get<RequestPreview>(`/api/request-preview?app=${encodeURIComponent(appName!)}`),
    enabled: !!appName,
  });

export const useCreateRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { app_name: string; justification?: string | null }) =>
      send<RequestPreview & { id: string; sent: boolean }>('/api/request', 'POST', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-requests'] }),
  });
};

// ── admin queries ───────────────────────────────────────────────────
export const useAdminSummary = () =>
  useQuery({
    queryKey: ['admin', 'summary'],
    queryFn: () =>
      get<{
        apps_count: number;
        total_matrix: number;
        total_actual: number;
        accounts_count: number;
        total_requests: number;
        open_requests: number;
      }>('/api/admin/summary'),
  });

export const useAdminApps = () =>
  useQuery({ queryKey: ['admin', 'apps'], queryFn: () => get<AdminApp[]>('/api/admin/apps') });

const invalidateApps = (qc: ReturnType<typeof useQueryClient>) => () => {
  qc.invalidateQueries({ queryKey: ['admin', 'apps'] });
  qc.invalidateQueries({ queryKey: ['apps'] });
  qc.invalidateQueries({ queryKey: ['admin', 'summary'] });
};

export const useInUseBulk = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (updates: { app_name: string; in_use: boolean }[]) =>
      send<{ ok: boolean; count: number }>('/api/admin/apps/in-use/bulk', 'POST', { updates }),
    onSuccess: invalidateApps(qc),
  });
};

export const usePatchApp = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, patch }: { name: string; patch: Record<string, unknown> }) =>
      // Names with '/' can't go in the URL path (the LB rejects %2F), so use
      // the name-in-body PATCH for those; plain names use the path route.
      name.includes('/')
        ? send<AdminApp>('/api/admin/apps', 'PATCH', { ...patch, app_name: name })
        : send<AdminApp>(`/api/admin/apps/${encodeURIComponent(name)}`, 'PATCH', patch),
    onSuccess: invalidateApps(qc),
  });
};

export const useCreateApp = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => send<AdminApp>('/api/admin/apps', 'POST', body),
    onSuccess: invalidateApps(qc),
  });
};

export const useDeleteApp = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      send<{ ok: boolean }>(`/api/admin/apps/${encodeURIComponent(name)}`, 'DELETE'),
    onSuccess: invalidateApps(qc),
  });
};

export const useBulkGrant = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { app_name: string; emails: string[] }) =>
      send<{ ok: boolean; granted: number }>('/api/admin/access/bulk', 'POST', body),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'app-users', vars.app_name] });
      qc.invalidateQueries({ queryKey: ['admin', 'apps'] });
    },
  });
};

export const useAppUsers = (appName: string | null) =>
  useQuery({
    queryKey: ['admin', 'app-users', appName],
    // Name in the query string, not the path — the LB rejects %2F in paths,
    // which 404'd slash-named apps (Atlassian (Jira / Confluence), HelloSign).
    queryFn: () => get<AppUserRow[]>(`/api/admin/app-users?app=${encodeURIComponent(appName!)}`),
    enabled: !!appName,
  });

export const useCategories = () =>
  useQuery({
    queryKey: ['admin', 'categories'],
    queryFn: () => get<{ category: string; app_count: number }[]>('/api/admin/categories'),
  });

export const useRenameCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { oldName: string; newName: string }) =>
      send<{ ok: boolean }>('/api/admin/categories/rename', 'POST', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      qc.invalidateQueries({ queryKey: ['apps'] });
    },
  });
};

export const useAdminRequests = () =>
  useQuery({
    queryKey: ['admin', 'requests'],
    queryFn: () => get<AdminRequest[]>('/api/admin/requests'),
  });

export const usePatchRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      send<{ ok: boolean }>(`/api/admin/requests/${encodeURIComponent(id)}`, 'PATCH', { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'requests'] }),
  });
};

export const useAdmins = () =>
  useQuery({
    queryKey: ['admin', 'admins'],
    queryFn: () => get<{ envAdmins: string[]; tableAdmins: string[] }>('/api/admin/admins'),
  });

export const useAddAdmin = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => send<{ ok: boolean }>('/api/admin/admins', 'POST', { email }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'admins'] }),
  });
};

export const useRemoveAdmin = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      send<{ ok: boolean }>(`/api/admin/admins/${encodeURIComponent(email)}`, 'DELETE'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'admins'] }),
  });
};

export const useOverrides = () =>
  useQuery({
    queryKey: ['admin', 'overrides'],
    queryFn: () =>
      get<{ user_email: string; manager_email: string; manager_name: string | null }[]>(
        '/api/admin/overrides',
      ),
  });

export const useUpsertOverride = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      user_email: string;
      manager_email: string;
      manager_name?: string | null;
    }) => send<{ ok: boolean }>('/api/admin/overrides', 'POST', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'overrides'] }),
  });
};

export const useDeleteOverride = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      send<{ ok: boolean }>(`/api/admin/overrides/${encodeURIComponent(email)}`, 'DELETE'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'overrides'] }),
  });
};

export const useHrStatus = () =>
  useQuery({
    queryKey: ['admin', 'hr-status'],
    queryFn: () => get<{ count: number; last_upload: string | null }>('/api/admin/hr/status'),
  });

export const useHrRoster = () =>
  useQuery({
    queryKey: ['admin', 'hr-roster'],
    queryFn: () => get<HRRosterRow[]>('/api/admin/hr/roster'),
  });

export const useHrUpload = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rows: Record<string, string | null>[]) =>
      send<{
        ok: boolean;
        rows_inserted: number;
        uploaded_at: string;
        email_sources?: Record<string, number>;
      }>('/api/admin/hr/upload', 'POST', { rows }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'hr-status'] });
      qc.invalidateQueries({ queryKey: ['admin', 'hr-roster'] });
    },
  });
};

export const useSyncStatus = () =>
  useQuery({
    queryKey: ['admin', 'sync-status'],
    queryFn: () => get<Record<string, unknown>>('/api/admin/sync-status'),
  });

// dashboard widgets
export const useSpendSummary = () =>
  useQuery({
    queryKey: ['admin', 'dash', 'spend-summary'],
    queryFn: () => get<{ ytd: number; prior: number }>('/api/admin/dashboard/spend-summary'),
  });

export const useTopSpend = () =>
  useQuery({
    queryKey: ['admin', 'dash', 'top-spend'],
    queryFn: () => get<ChartPoint[]>('/api/admin/dashboard/top-spend?limit=10'),
  });

export const useSpendByCategory = () =>
  useQuery({
    queryKey: ['admin', 'dash', 'spend-by-category'],
    queryFn: () => get<ChartPoint[]>('/api/admin/dashboard/spend-by-category'),
  });

export const useUserDistribution = () =>
  useQuery({
    queryKey: ['admin', 'dash', 'user-distribution'],
    queryFn: () => get<ChartPoint[]>('/api/admin/dashboard/user-distribution'),
  });

export const useCoverage = () =>
  useQuery({
    queryKey: ['admin', 'dash', 'coverage'],
    queryFn: () => get<ChartPoint[]>('/api/admin/dashboard/coverage'),
  });

export interface AuditEntry {
  at: string;
  actor: string;
  action: string;
  resource_type: string;
  resource_id: string;
  fields: string[];
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}

export const useAuditLog = () =>
  useQuery({
    queryKey: ['admin', 'audit'],
    queryFn: () => get<AuditEntry[]>('/api/admin/audit'),
  });

export interface DirectoryUser {
  email: string;
  name: string | null;
  dept: string | null;
  app_count: number;
}

// Active directory users — for owner lookup fields (SGO 06/15).
export const useDirectory = () =>
  useQuery({
    queryKey: ['directory'],
    queryFn: () => get<DirectoryUser[]>('/api/users'),
    staleTime: 5 * 60_000,
  });
